import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tile } from 'foundations-components/transitional/components/Tile';
import _capitalize from 'hs-lodash/capitalize';
import _isNil from 'hs-lodash/isNil';
import _omitBy from 'hs-lodash/omitBy';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ACTIONS, SCREENS } from '../../../constants/Tracking';
import { NON_HOST_EXPERIENCE_REFRESH_TIMEOUT } from '../../../constants/timeouts';
import { associationTypesToCall, pluralTypeToSingle, singularTypeToPlural } from '../../../constants/types';
import { AppContext } from '../../../contexts/app/context';
import { AssociationsPropsContext } from '../../../contexts/associations/PropsContext';
import { defaultAssociationsContextProps } from '../../../contexts/associations/constants';
import { AssociationsContext, AssociationsContextProvider } from '../../../contexts/associations/context';
import { PropertiesContext } from '../../../contexts/properties/context';
import PropertiesContextHOC from '../../../hoc/PropertiesContextHOC';
import useAssociationModal from '../../../hooks/useAssociationModal';
import useAssociationsSections from '../../../hooks/useAssociationsSections';
import useInterval from '../../../hooks/useInterval';
import usePreviewModal from '../../../hooks/usePreviewModal';
import AssociateExistingCrmObjectTab from '../../AssociationModal/Tabs/AssociateExistingCrmObjectTab';
import FeaturePopupOver from '../../common/FeaturePopupOver';
import HoverActions from '../../common/HoverActions/HoverActions';
import PlaybookListInModal from '../../common/playbooks/PlaybookListInModal';
import AssociationSection from '../AssociationSection';
import CrmObjectCard from '../CrmObjectCard';
import ObjectCardButtons from '../ObjectCardButtons';
import RemoveAssociationModal from '../RemoveAssociationModal';
import { Container } from './Associations.style';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function Associations() {
  const {
    callObjectId,
    callObjectTitle,
    openUrl,
    initMethod,
    usageTracker,
    hubspotUserId,
    portalId,
    apiWrapper,
    isContactsPage,
    createObjectCallback,
    hasLegacyDealAmountCalculation,
    featureTipsLink,
    prosemirrorTrackerProperties,
    reconnectRoute
  } = useContext(AssociationsPropsContext);
  const {
    isLoading,
    crmObjectType,
    associationsActions,
    associations
  } = useContext(AssociationsContext);
  const {
    properties,
    isPorpertiesLoading
  } = useContext(PropertiesContext);
  const {
    isPastMeeting,
    appName,
    features
  } = useContext(AppContext);
  const sections = useAssociationsSections();
  const [removeCandidateObject, setRemoveCandidateObject] = useState(null);
  const [associationCandidates, setAssociationCandidates] = useState([]);
  const [selectedObjectId, setSelectedObjectId] = useState('');
  const attachObject = useCallback(() => {
    associationsActions.associateObjects({
      associationCandidates,
      callObjectId,
      objectType: singularTypeToPlural[crmObjectType],
      featuresStatus: features,
      associations
    });
  }, [callObjectId, crmObjectType, features, associationCandidates, associationsActions, associations]);
  const createObjectHandler = data => {
    associationsActions.setCreatedObject(data);
    if (typeof createObjectCallback === 'function') {
      createObjectCallback(data);
    } else {
      associationsActions.associateObjects({
        associationCandidates: [JSON.stringify({
          id: data.entity.id
        })],
        callObjectId,
        objectType: singularTypeToPlural[crmObjectType],
        featuresStatus: features,
        associations
      });
    }
  };
  const updateCrmObjectCallback = data => {
    associationsActions.updateAccosiatedObject(data);
  };
  const [openAssociationModal, AssociationsModal] = useAssociationModal({
    hubspotUserId,
    portalId,
    crmObjectType,
    openUrl,
    usageTracker,
    onSubmitAssociation: attachObject,
    isConfirmButtonEnabled: Boolean(associationCandidates.length),
    associateCrmObjectComponent: /*#__PURE__*/_jsx(AssociateExistingCrmObjectTab, {
      objectType: singularTypeToPlural[crmObjectType],
      setAssociationCandidates: setAssociationCandidates,
      appName: appName,
      searchObjectApi: apiWrapper.searchObject
    }),
    apiWrapper,
    isContactsPage,
    lineItemsFeatureUnavailableComponent: features.CREATING_DEALS_WITH_LINE_ITEMS.available ? null : /*#__PURE__*/_jsxs(TFlex, {
      align: "center",
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "associationsPanel.tabs.create.associationProperties.featureDisabled"
      }), /*#__PURE__*/_jsx(FeaturePopupOver, {
        portalId: portalId,
        featureDisabledReason: features.CREATING_DEALS_WITH_LINE_ITEMS.reason,
        openUrl: openUrl,
        featureTipsLink: featureTipsLink,
        reconnectRoute: reconnectRoute
      })]
    }, "line-items-unavailable"),
    createObjectCallback: createObjectHandler,
    callObjectId,
    hasLegacyDealAmountCalculation,
    prosemirrorTrackerProperties
  });
  const [openPreviewModal, PreviewModal] = usePreviewModal({
    portalId,
    openUrl,
    usageTracker,
    crmObjectType,
    extensionComponents: [/*#__PURE__*/_jsx(PlaybookListInModal, {
      callObjectId: Number(callObjectId),
      portalId: portalId,
      selectedObjectType: singularTypeToPlural[crmObjectType],
      selectedObjectId: selectedObjectId,
      openUrl: openUrl,
      usageTracker: usageTracker,
      apiWrapper: apiWrapper.playbooks,
      featureTipsLink: featureTipsLink,
      reconnectRoute: reconnectRoute
    }, "playbooks")],
    apiWrapper,
    updateCrmObjectCallback,
    callObjectId,
    prosemirrorTrackerProperties,
    hubspotUserId
  });
  const openObjectDirectLink = useCallback((event, url, objectType) => {
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS[`associated${_capitalize(pluralTypeToSingle[objectType])}`]
    }, ACTIONS.associations.clickOpen));
    event.preventDefault();
    event.stopPropagation();
    openUrl(url);
  }, [openUrl, usageTracker]);
  const onPreviewClick = useCallback((item, type) => {
    associationsActions.setCrmObjectType(pluralTypeToSingle[type]);
    setSelectedObjectId(item.id);
    openPreviewModal(item.id);
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS[`associated${_capitalize(pluralTypeToSingle[type])}`],
      type: pluralTypeToSingle[type]
    }, ACTIONS.general.clickPreview));
  }, [openPreviewModal, usageTracker, associationsActions]);
  const onRemoveClick = useCallback((item, type) => {
    const data = {
      objectType: type,
      toObjectId: callObjectId,
      associationType: associationTypesToCall[type]
    };
    associationsActions.disassociateObject(Object.assign({}, data, {
      objectId: item.id
    }));
  }, [callObjectId, associationsActions]);
  const getFeature = object => features[object.feature];
  const getAssociationSectionList = useCallback(params => {
    if (callObjectId) {
      associationsActions.getAssociationSectionList(Object.assign({
        callObjectId
      }, params));
    }
  }, [callObjectId, associationsActions]);
  useInterval(setSkipRefresh => getAssociationSectionList({
    featuresStatus: features,
    isLoading: false,
    isPostponedAction: true,
    setSkipRefresh,
    associations
  }), NON_HOST_EXPERIENCE_REFRESH_TIMEOUT, {
    cancelOnUnmount: true
  });
  useEffect(() => {
    getAssociationSectionList({
      featuresStatus: features,
      associations
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAssociationSectionList, features]);
  useEffect(() => {
    usageTracker.trackPageView(SCREENS.associations);
  }, [usageTracker]);
  useEffect(() => {
    initMethod();
  }, [initMethod]);
  const sectionContent = useCallback(object => {
    if ((isLoading || isPorpertiesLoading) && !object.list.length) return I18n.text(`associationList.sectionEmptyState.${object.type}`);
    if (object.list.length) {
      return object.list.map(item => /*#__PURE__*/_jsx(Tile, {
        compact: true,
        distance: "extra-small",
        children: /*#__PURE__*/_jsx(HoverActions, {
          Buttons: ObjectCardButtons,
          buttonsProps: {
            openObjectDirectLink,
            onPreviewClick,
            item,
            type: object.type,
            setRemoveCandidateObject,
            portalId,
            readonly: isPastMeeting,
            appName
          },
          appName: appName,
          children: /*#__PURE__*/_jsx(CrmObjectCard, {
            onPreviewClick: onPreviewClick,
            item: item,
            type: object.type,
            propertiesDetails: properties[pluralTypeToSingle[object.type]].detailedPropertiesInformation,
            propertyKeysToShow: properties[pluralTypeToSingle[object.type]].associationCardProperties
          })
        })
      }, item.id));
    }
    return I18n.text(`associationList.sectionEmptyState.${object.type}`);
  }, [isLoading, properties, onPreviewClick, openObjectDirectLink, portalId, appName, isPorpertiesLoading, isPastMeeting]);
  const removeAssociationModal = useMemo(() => {
    if (removeCandidateObject) {
      return /*#__PURE__*/_jsx(RemoveAssociationModal, {
        setRemoveCandidateObject: setRemoveCandidateObject,
        onRemoveClick: onRemoveClick,
        item: removeCandidateObject.item,
        type: removeCandidateObject.type,
        callObjectTitle: callObjectTitle,
        appName: appName
      });
    }
    return null;
  }, [callObjectTitle, onRemoveClick, removeCandidateObject, appName]);
  return /*#__PURE__*/_jsx(Container, {
    "data-test-id": `${appName}-associations-page`,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [isLoading || isPorpertiesLoading ? /*#__PURE__*/_jsx(LoadingOverlay, {
        contextual: true
      }) : null, sections.map(object => /*#__PURE__*/_jsx(AssociationSection, {
        objectType: object.type,
        disabled: !getFeature(object).available,
        feature: features[object.feature],
        openAssociationModal: openAssociationModal,
        children: sectionContent(object)
      }, object.type)), removeAssociationModal, AssociationsModal, PreviewModal]
    })
  });
}
const WrappedAssociations = props => {
  const definedProps = _omitBy(props, _isNil);
  const apiWrapper = _omitBy(props.apiWrapper, _isNil);
  const properties = Object.assign({}, defaultAssociationsContextProps, definedProps, {
    apiWrapper: Object.assign({}, defaultAssociationsContextProps.apiWrapper, apiWrapper)
  });
  return /*#__PURE__*/_jsx(PropertiesContextHOC, {
    apiWrapper: {
      fetchExternalOptions: properties.apiWrapper.fetchExternalOptions,
      validateProperty: properties.apiWrapper.validateProperty,
      fetchCardProperties: properties.apiWrapper.fetchCardProperties,
      fetchPropertiesDetails: properties.apiWrapper.fetchPropertiesDetails
    },
    children: /*#__PURE__*/_jsx(AssociationsPropsContext.Provider, {
      value: properties,
      children: /*#__PURE__*/_jsx(AssociationsContextProvider, {
        children: /*#__PURE__*/_jsx(Associations, {})
      })
    })
  });
};
export default WrappedAssociations;